/**
 * Hero Avatar Assets
 *
 * Character avatars for each Tonnie hero.
 * Used in dashboard cards, headers, and hero-specific pages.
 */

import type { AssetPath, HeroType, SizeConfig } from '../types';

/**
 * Base path for hero assets
 */
const HEROES_BASE = '/assets/images/heroes';

/**
 * Hero avatar size presets (in pixels)
 */
export const HERO_AVATAR_SIZES: SizeConfig = {
  xs: 32,
  sm: 48,
  md: 96,
  lg: 144,
  xl: 184,
  '2xl': 256,
} as const;

/**
 * Hero avatar assets with metadata
 * Note: passport uses the hero illustration as no separate avatar exists
 */
export const HERO_AVATAR_ASSETS: Record<HeroType, AssetPath> = {
  checkup: {
    path: `${HEROES_BASE}/avatar-checkup.png`,
    alt: 'CheckUp Hero - Assessment assistant',
    width: 256,
    height: 256,
  },
  teleportal: {
    path: `${HEROES_BASE}/avatar-teleportal.png`,
    alt: 'TelePortal Hero - Mobility guide',
    width: 256,
    height: 256,
  },
  qultureup: {
    path: `${HEROES_BASE}/avatar-qultureup.png`,
    alt: 'QultureUp Hero - Culture coach',
    width: 256,
    height: 256,
  },
  care: {
    path: `${HEROES_BASE}/avatar-care.png`,
    alt: 'Care Hero - Wellbeing support',
    width: 256,
    height: 256,
  },
  passport: {
    path: `${HEROES_BASE}/hero-passport.png`,
    alt: 'Passport Hero - Journey record keeper',
    width: 256,
    height: 256,
  },
} as const;

/**
 * Hero avatar paths (simple string access)
 */
export const HERO_AVATARS: Record<HeroType, string> = {
  checkup: HERO_AVATAR_ASSETS.checkup.path,
  teleportal: HERO_AVATAR_ASSETS.teleportal.path,
  qultureup: HERO_AVATAR_ASSETS.qultureup.path,
  care: HERO_AVATAR_ASSETS.care.path,
  passport: HERO_AVATAR_ASSETS.passport.path,
} as const;

/**
 * CoPilot avatar asset
 */
export const COPILOT_AVATAR: AssetPath = {
  path: `${HEROES_BASE}/avatar-copilot.png`,
  alt: 'CoPilot AI Assistant',
  width: 256,
  height: 256,
};

/**
 * Hero illustration assets (larger images for pages/cards)
 * These are the full hero character illustrations
 */
export const HERO_ILLUSTRATIONS: Record<HeroType, AssetPath> = {
  checkup: {
    path: `${HEROES_BASE}/hero-checkup.png`,
    alt: 'CheckUp Hero illustration',
    width: 400,
    height: 400,
  },
  teleportal: {
    path: `${HEROES_BASE}/hero-teleportal.png`,
    alt: 'TelePortal Hero illustration',
    width: 400,
    height: 400,
  },
  qultureup: {
    path: `${HEROES_BASE}/hero-qultureup.png`,
    alt: 'QultureUp Hero illustration',
    width: 400,
    height: 400,
  },
  care: {
    path: `${HEROES_BASE}/hero-care.png`,
    alt: 'Care Hero illustration',
    width: 400,
    height: 400,
  },
  passport: {
    path: `${HEROES_BASE}/hero-passport.png`,
    alt: 'Passport Hero illustration',
    width: 400,
    height: 400,
  },
} as const;

/**
 * Hero illustration paths (simple string access)
 */
export const HERO_ILLUSTRATION_PATHS: Record<HeroType, string> = {
  checkup: HERO_ILLUSTRATIONS.checkup.path,
  teleportal: HERO_ILLUSTRATIONS.teleportal.path,
  qultureup: HERO_ILLUSTRATIONS.qultureup.path,
  care: HERO_ILLUSTRATIONS.care.path,
  passport: HERO_ILLUSTRATIONS.passport.path,
} as const;

/**
 * Get hero avatar path
 */
export function getHeroAvatar(hero: HeroType): string {
  return HERO_AVATARS[hero];
}

/**
 * Get hero avatar asset with metadata
 */
export function getHeroAvatarAsset(hero: HeroType): AssetPath {
  return HERO_AVATAR_ASSETS[hero];
}

/**
 * Get hero illustration path
 */
export function getHeroIllustration(hero: HeroType): string {
  return HERO_ILLUSTRATIONS[hero].path;
}

/**
 * Get hero illustration asset with metadata
 */
export function getHeroIllustrationAsset(hero: HeroType): AssetPath {
  return HERO_ILLUSTRATIONS[hero];
}

/**
 * Get CoPilot avatar path
 */
export function getCoPilotAvatar(): string {
  return COPILOT_AVATAR.path;
}
